// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Dashboard API Client
 *
 * API functions for the Advisor Dashboard feature.
 * Uses the base axios instance configured in advisorApi.ts
 *
 * Alerts are served by the Alerts & Insights Service (/api/alerts),
 * other dashboard data (summary, activity, performance) from Resource Server.
 */

import api from './advisorApi';
import type {
  AdvisorDashboardResponse,
  DashboardSummaryParams,
  DashboardAlertsResponse,
  DashboardAlertsParams,
  DashboardActivityResponse,
  DashboardActivityParams,
  AdvisorPerformanceResponse,
  AdvisorPerformanceParams,
  ResendInviteResponse,
  DashboardAlert,
} from '../types/dashboard';
import type { DashboardInsight } from '../components/dashboard/DashboardAlertsPanel';

export interface DashboardInsightsResponse {
  insights: DashboardInsight[];
  totalCount: number;
}

export interface DashboardInsightsParams {
  all?: boolean;
  advisorId?: string;
  profileId?: string;
  limit?: number;
}

/** Response shape from the Alerts & Insights Service */
interface AlertsServicePageResponse {
  alerts: AlertsServiceAlertDTO[];
  totalCount: number;
  limit: number;
  offset: number;
}

/** Response shape from the Alerts & Insights Service summary endpoint */
interface AlertsServiceSummaryResponse {
  urgentCount: number;
  warningCount: number;
  infoCount: number;
  totalCount: number;
  generatedAt: string;
}

/** Alert DTO from the Alerts & Insights Service */
interface AlertsServiceAlertDTO {
  id: string;
  tenantId: string;
  advisorId: string;
  advisorName: string;
  alertType: string;
  severity: string;
  title: string;
  description: string;
  resourceId: string;
  resourceType: string;
  resourceName: string;
  profileId: string;
  profileName: string;
  daysSinceEvent: number;
  eventTimestamp: string;
  actions: Array<{
    type: string;
    label: string;
    method: string;
    endpoint?: string;
    href?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

/** Map alerts-service DTO to the existing UI DashboardAlert interface */
function mapAlertDTO(dto: AlertsServiceAlertDTO): DashboardAlert {
  return {
    id: dto.id,
    type: dto.alertType as DashboardAlert['type'],
    severity: dto.severity as DashboardAlert['severity'],
    title: dto.title,
    description: dto.description,
    resourceId: dto.resourceId,
    resourceType: dto.resourceType as DashboardAlert['resourceType'],
    resourceName: dto.resourceName,
    profileId: dto.profileId,
    advisorId: dto.advisorId,
    advisorName: dto.advisorName,
    daysSinceEvent: dto.daysSinceEvent,
    eventTimestamp: dto.eventTimestamp,
    actions: (dto.actions || []).map(a => ({
      type: a.type as DashboardAlert['actions'][0]['type'],
      label: a.label,
      method: a.method as DashboardAlert['actions'][0]['method'],
      endpoint: a.endpoint,
      href: a.href,
    })),
  };
}

export const dashboardApi = {
  /**
   * Get dashboard summary including KPIs, health stats, navigation counts, and top providers.
   *
   * @param params Query parameters for view scope
   * @returns Dashboard summary data
   */
  getSummary: async (params: DashboardSummaryParams = {}): Promise<AdvisorDashboardResponse> => {
    const response = await api.get<AdvisorDashboardResponse>('/dashboard/advisor', {
      params: {
        all: params.all ?? false,
        advisorId: params.advisorId ?? undefined,
      },
    });
    return response.data;
  },

  /**
   * Get dashboard alerts with actions.
   * Tries the Alerts & Insights Service first (/api/alerts + /api/alerts/summary).
   * Falls back to the Resource Server (/api/dashboard/alerts) if the alerts-service
   * is unavailable (404, network error, or not enabled in BFF).
   *
   * @param params Query parameters for filtering alerts
   * @returns Dashboard alerts data
   */
  getAlerts: async (params: DashboardAlertsParams = {}): Promise<DashboardAlertsResponse> => {
    try {
      const queryParams: Record<string, string | number | boolean | undefined> = {
        advisorId: params.advisorId ?? undefined,
        profileId: params.profileId ?? undefined,
        limit: params.limit ?? 10,
        offset: 0,
        severity: params.severity ?? undefined,
      };

      const [alertsResponse, summaryResponse] = await Promise.all([
        api.get<AlertsServicePageResponse>('/alerts', { params: queryParams }),
        api.get<AlertsServiceSummaryResponse>('/alerts/summary', {
          params: {
            advisorId: params.advisorId ?? undefined,
            profileId: params.profileId ?? undefined,
          },
        }),
      ]);

      const alerts = alertsResponse.data;
      const summary = summaryResponse.data;

      return {
        alerts: (alerts.alerts || []).map(mapAlertDTO),
        totalCount: alerts.totalCount,
        urgentCount: summary.urgentCount,
        warningCount: summary.warningCount,
        infoCount: summary.infoCount,
        generatedAt: summary.generatedAt,
      };
    } catch {
      // Fallback: alerts-service unavailable — use Resource Server endpoint
      const response = await api.get<DashboardAlertsResponse>('/dashboard/alerts', {
        params: {
          all: params.all ?? false,
          advisorId: params.advisorId ?? undefined,
          profileId: params.profileId ?? undefined,
          limit: params.limit ?? 10,
          severity: params.severity ?? undefined,
        },
      });
      return response.data;
    }
  },

  /**
   * Get recent activity feed.
   *
   * @param params Query parameters for filtering activity
   * @returns Dashboard activity data
   */
  getActivity: async (params: DashboardActivityParams = {}): Promise<DashboardActivityResponse> => {
    const response = await api.get<DashboardActivityResponse>('/dashboard/activity', {
      params: {
        all: params.all ?? false,
        advisorId: params.advisorId ?? undefined,
        limit: params.limit ?? 10,
        days: params.days ?? 7,
      },
    });
    return response.data;
  },

  /**
   * Get advisor performance metrics (admin only).
   *
   * @param params Query parameters for sorting
   * @returns Advisor performance data
   */
  getAdvisorPerformance: async (params: AdvisorPerformanceParams = {}): Promise<AdvisorPerformanceResponse> => {
    const response = await api.get<AdvisorPerformanceResponse>('/dashboard/advisor-performance', {
      params: {
        limit: params.limit ?? 10,
        sortBy: params.sortBy ?? 'profileCount',
        sortDir: params.sortDir ?? 'desc',
      },
    });
    return response.data;
  },

  /**
   * Resend invitation email to a client.
   *
   * @param clientId The client profile ID
   * @param customMessage Optional custom message to include
   * @returns Resend response with status
   */
  resendInvite: async (clientId: string, customMessage?: string): Promise<ResendInviteResponse> => {
    const response = await api.post<ResendInviteResponse>(
      `/clients/${clientId}/resend-invite`,
      customMessage ? { customMessage } : {}
    );
    return response.data;
  },


  /**
   * Get dashboard insights.
   *
   * @param params Query parameters for filtering insights
   * @returns Dashboard insights data
   */
  getInsights: async (params: DashboardInsightsParams = {}): Promise<DashboardInsightsResponse> => {
    const response = await api.get<DashboardInsightsResponse>('/dashboard/insights', {
      params: {
        all: params.all ?? false,
        advisorId: params.advisorId ?? undefined,
        profileId: params.profileId ?? undefined,
        limit: params.limit ?? 5,
      },
    });
    return response.data;
  },
};

export default dashboardApi;
